import axios from "axios";
import { getSocket } from "../../utils/useChatSocket";
import { getValidChatSession, saveChatSession, getValidDexySession, saveDexySession } from "../../utils/sessionManager";

const socket = getSocket();

export const SET_CONVO = "SET_CONVO";
export const UPDATE_CONVO_MESSAGES = "UPDATE_CONVO_MESSAGES";
export const UPDATE_CONVO_STATUS = "UPDATE_CONVO_STATUS";

export const setConvo = (convo) => ({
  type: SET_CONVO,
  payload: convo,
});

export const updateMessage = (message) => ({
  type: UPDATE_CONVO_MESSAGES,
  message: message,
});

export const updateStatus = (status) => ({
  type: UPDATE_CONVO_STATUS,
  status: status,
});

// Map to track active in-flight createConvo requests to prevent concurrent duplicate API calls
const activeCreationRequests = new Map();
const activeDexyCreationRequests = new Map();

export const createDexyConvo = (widgetConfigs, options = {}) => async (dispatch) => {
  const uniquePartnerId = widgetConfigs?.uniquePartnerId;
  if (!uniquePartnerId) {
    console.warn("createDexyConvo called without uniquePartnerId");
    return null;
  }

  // 1. Check if a valid Dexy session already exists in localStorage (< 24 hours)
  const existingSession = getValidDexySession(uniquePartnerId);
  if (existingSession && existingSession.conversation && !options.forceNew) {
    console.log("♻️ Reusing existing 24-hour Dexy session:", existingSession.ticketId);
    if (socket && existingSession.conversation.roomId) {
      socket.emit("join", [existingSession.conversation.roomId]);
    }
    return existingSession.conversation;
  }

  if (activeDexyCreationRequests.has(uniquePartnerId) && !options.forceNew) {
    console.log("⏳ createDexyConvo already in-flight for this partner, reusing request...");
    return await activeDexyCreationRequests.get(uniquePartnerId);
  }

  const creationPromise = (async () => {
    try {
      const customerDetails =
        JSON.parse(localStorage.getItem("dexkor-chat-widget-customer-details")) || {};

      const emailVal = (customerDetails.email || widgetConfigs.userEmail || "").trim();
      const emailPrefix = emailVal && emailVal.includes("@") ? emailVal.split("@")[0].trim() : "";
      const phoneVal = (customerDetails.phone || customerDetails.number || widgetConfigs.userNumber || "").trim();
      const userNameVal = (customerDetails.name || widgetConfigs.userName || "").trim();

      const computedSubject =
        options.subject ||
        emailPrefix ||
        phoneVal ||
        userNameVal ||
        "Unknown";

      const response = await axios.post(
        `${process.env.REACT_APP_CHAT_MICROSERVICE_URL}/tickets/create`,
        {
          uniquePartnerId: uniquePartnerId,
          name: customerDetails.name || widgetConfigs.userName || "Customer",
          number: customerDetails.phone || widgetConfigs.userNumber || "",
          email: customerDetails.email || widgetConfigs.userEmail || "",
          customerCode: customerDetails.customerCode || widgetConfigs.customerCode || "",
          subject: computedSubject,
          category: options.category || "",
          status: "unassigned",
          source: "widget",
          subSource: "convo",
          botHandled: true,
          type: "inbox",
          priority: "medium",
          description: options.description || "Customer chat with Dexy AI",
          customFields: {
            isDexyAiConvo: true,
            convoType: "dexy_ai",
          },
        }
      );

      const roomId = `DexyRoom-${new Date().getTime()}`;
      const customerId = customerDetails._id || null;

      return await new Promise((resolve) => {
        socket.emit(
          "createRoom",
          uniquePartnerId,
          roomId,
          response.data._id,
          customerId,
          null,
          (res) => {
            if (res && res.success) {
              const convoObj = res.conversation || {
                roomId: roomId,
                ticketId: response.data._id,
                uniquePartnerId: uniquePartnerId,
                messages: [],
              };

              saveDexySession(uniquePartnerId, {
                ticketId: response.data._id,
                ticketNumber: response.data.ticketNumber,
                roomId: roomId,
                conversation: convoObj,
              });

              if (socket) {
                socket.emit("join", [roomId]);
              }

              resolve(convoObj);
            } else {
              resolve(null);
            }
          }
        );
      });
    } catch (error) {
      console.error("Error occurred while creating Dexy convo", error);
      return null;
    } finally {
      activeDexyCreationRequests.delete(uniquePartnerId);
    }
  })();

  activeDexyCreationRequests.set(uniquePartnerId, creationPromise);
  return await creationPromise;
};

export const createConvo = (widgetConfigs, options = {}) => async (dispatch) => {
  const uniquePartnerId = widgetConfigs?.uniquePartnerId;
  if (!uniquePartnerId) {
    console.warn("createConvo called without uniquePartnerId");
    return;
  }

  // 1. Check if a valid session already exists in localStorage (< 24 hours) for this workflow
  const existingSession = getValidChatSession(uniquePartnerId, options.workflowId);
  if (existingSession && existingSession.conversation && !options.forceNew) {
    console.log("♻️ Reusing existing 24-hour chat session. No new ticket created:", existingSession.ticketId);
    dispatch(setConvo(existingSession.conversation));
    if (socket && existingSession.conversation.roomId) {
      socket.emit("join", [existingSession.conversation.roomId]);
    }
    return existingSession.conversation;
  }

  // 2. If a creation request is already in flight for this partner, wait for it instead of duplicating
  if (activeCreationRequests.has(uniquePartnerId) && !options.forceNew) {
    console.log("⏳ createConvo already in-flight for this partner, reusing in-flight request...");
    return await activeCreationRequests.get(uniquePartnerId);
  }

  // 3. Launch single creation request with mutex protection
  const creationPromise = (async () => {
    try {
      const customerDetails =
        JSON.parse(localStorage.getItem("dexkor-chat-widget-customer-details")) || {};

      const emailVal = (customerDetails.email || widgetConfigs.userEmail || "").trim();
      const emailPrefix = emailVal && emailVal.includes("@") ? emailVal.split("@")[0].trim() : "";
      const phoneVal = (customerDetails.phone || customerDetails.number || widgetConfigs.userNumber || "").trim();
      const userNameVal = (customerDetails.name || widgetConfigs.userName || "").trim();

      const computedSubject =
        options.subject ||
        emailPrefix ||
        phoneVal ||
        userNameVal ||
        "Unknown";

      const response = await axios.post(
        `${process.env.REACT_APP_CHAT_MICROSERVICE_URL}/tickets/create`,
        {
          uniquePartnerId: uniquePartnerId,
          name: customerDetails.name || widgetConfigs.userName || "",
          number: customerDetails.phone || widgetConfigs.userNumber || "",
          email: customerDetails.email || widgetConfigs.userEmail || "",
          customerCode: customerDetails.customerCode || widgetConfigs.customerCode || "",
          subject: computedSubject,
          category: options.category || "",
          status: "unassigned",
          source: "widget",
          subSource: "convo",
          botHandled: true,
          type: "inbox",
          priority: "medium",
          description: options.description || "",
          customFields: {
            isAutomatedConvo: options.automated !== false,
            convoType: "live_chat",
          },
          ...(Array.isArray(options.cc) &&
            options.cc.filter((c) => c && typeof c.address === "string" && c.address.trim().length > 0).length > 0
            ? {
              cc: options.cc.filter(
                (c) => c && typeof c.address === "string" && c.address.trim().length > 0
              ),
            }
            : {}),
        }
      );

      const roomId = `Room-${new Date().getTime()}`;
      const customerId = customerDetails._id || null;

      return await new Promise((resolve) => {
        socket.emit(
          "createRoom",
          uniquePartnerId,
          roomId,
          response.data._id,
          customerId,
          null,
          (res) => {
            if (res.success) {
              let autoHistory = [];
              try {
                const rawHistory = localStorage.getItem(`dexkor_automated_history_${uniquePartnerId}`);
                if (rawHistory) autoHistory = JSON.parse(rawHistory);
              } catch (e) { }

              const convoWithWorkflow = {
                ...(res.conversation || {}),
                workflowId: options.workflowId || null,
              };

              saveChatSession(uniquePartnerId, {
                ticketId: response.data._id,
                ticketNumber: response.data.ticketNumber,
                roomId: roomId,
                conversation: convoWithWorkflow,
                workflowId: options.workflowId || null,
                botCompleted: Boolean(options.botCompleted),
                automatedHistory: options.workflowId === 'legacy' ? autoHistory : [],
              });

              dispatch(setConvo(convoWithWorkflow));
              resolve(convoWithWorkflow);
            } else {
              resolve(null);
            }
          }
        );
      });
    } catch (error) {
      console.error("Error occurred while creating convo", error);
      return null;
    } finally {
      activeCreationRequests.delete(uniquePartnerId);
    }
  })();

  activeCreationRequests.set(uniquePartnerId, creationPromise);
  return await creationPromise;
};

export const updateConvoMessage = (message) => async (dispatch) => {
  if (message) {
    dispatch(updateMessage(message));
  } else {
    console.log("message missing!");
  }
};

export const updateConvoStatus = (status) => async (dispatch) => {
  if (status) {
    dispatch(updateStatus(status));
  } else {
    console.log("status missing!");
  }
};
